import myDayStyles from "./myDay.module.css";
import venchy from "../../images/questionnaire_doctor.png";
import { filterFuturedAppointments, getPrintDate } from "../../utils/utils";
import Pagination from "../pagination/pagination";
import { useAppSelector } from "../../services/hooks";
import {
  useGetCurrentQuestionnaireByUsersIdQuery,
  useGetMessageByUserIdQuery,
  useGetExercisesByUserGuidMutation,
  useGetAdminMessagesQuery,
} from "../../services/api/user.api";
import { userSelector } from "../../services/selectors/user.selectors";
import React, { useEffect, useMemo, useState } from "react";
import Slider from "../slider/slider";
import { getMyDayUpdates } from "../../utils/constants";
import { tWithKeyAttr as t } from "../../i18nHelper";
import i18n from "../../i18n";
import { IAdminMessage } from "../../types/types";
import useMediaQuery from "../../hooks/useMediaQuery";
import { useContactAccess } from "../../utils/contactAccess";

function MyDay() {
  const mobile = useMediaQuery("(max-width: 810px)");

  const user = useAppSelector(userSelector);
  const [activeUpdatesIndex, setActiveUpdatesIndex] = useState(0);
  const [activeAppointmentsIndex, setActiveAppointmentsIndex] = useState(0);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);

  const { data: adminMessages } = useGetAdminMessagesQuery(
    { companyId: user.companyId },
    { skip: !user.companyId }
  );

  const displayUpdates = useMemo(() => {
    const cleanText = (text: string): string => {
      if (!text) return '';
      return text
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .trim();
    };

    const activeMessages = (adminMessages || []).filter(
      (msg: IAdminMessage) => msg.StatusId !== 0
    );

    if (activeMessages.length === 0) {
      const fallback = getMyDayUpdates();
      return fallback.slice(0, 1).map((item, index) => ({
        id: index,
        text: [item.title, item.desc].filter(Boolean).join('\n'),
        date: new Date(),
      }));
    }

    return activeMessages.map((msg: IAdminMessage) => {
      const title = cleanText(msg.Title || '');
      const desc = cleanText(msg.Desc || '');
      const combinedText = [title, desc].filter(Boolean).join('\n');
      return {
        id: msg.Id,
        text: combinedText,
        date: new Date(msg.UpdatedAt),
      };
    });
  }, [adminMessages]);

  // A contact acting for the patient may be limited; each part of the day follows its area.
  const access = useContactAccess();

  useGetMessageByUserIdQuery();
  useGetCurrentQuestionnaireByUsersIdQuery(
    {
      guid: user.guid,
      userId: Number(user.id),
    },
    { refetchOnMountOrArgChange: true, skip: !access.questionnairesFill }
  );

  const [getExercises, { data: exercises = [] }] =
    useGetExercisesByUserGuidMutation();

  useEffect(() => {
    if (user?.guid && access.exercisesView) {
      getExercises();
    }
  }, [user?.guid, getExercises, access.exercisesView]);

  const userAppointments = useMemo(() => {
    if (!user.appointments) return [];
    return filterFuturedAppointments(
      user.appointments.filter((item) => item.appointments.statusId)
    ).sort(
      (a, b) =>
        new Date(a.appointments.appointmentDate).getTime() -
        new Date(b.appointments.appointmentDate).getTime()
    );
  }, [user.appointments]);

  return (
    <div dir={i18n.dir()} className={myDayStyles.myDay}>
      <h3 className={myDayStyles.myDay__title}>
        {t("A_FEW_THINGS_ESPECIALLY_FOR_YOU")}
      </h3>
      <div className={myDayStyles.myDay__section}>
        <h4 className={myDayStyles.myDay__sectionTitle}>{t("UPDATES")}</h4>
        <div dir={i18n.dir()} className={myDayStyles.notifications}>
          <div className={myDayStyles.notifications__venchy}>
            <img
              src={venchy}
              className={myDayStyles.notifications__img}
              alt={t('IMAGE_OF_VENUS')}
            />
            <div>
              <p className={myDayStyles.notifications__name}>{t("WONCHI")}</p>
              <p className={myDayStyles.notifications__date}>
                {displayUpdates.length > 0 && displayUpdates[activeUpdatesIndex]?.date
                  ? getPrintDate(displayUpdates[activeUpdatesIndex].date)
                  : getPrintDate(new Date())}
              </p>
            </div>
          </div>
          <Slider
            items={displayUpdates}
            className={myDayStyles.notifications__text}
            activeIndex={activeUpdatesIndex}
            setActiveIndex={setActiveUpdatesIndex}
          />
          {displayUpdates.length > 1 && (
            <>
              {i18n.dir() === 'ltr' ? (
                <>
                  <button
                    className={myDayStyles.nextBtn}
                    onClick={() =>
                      setActiveUpdatesIndex((prevIndex) =>
                        prevIndex === 0 ? displayUpdates.length - 1 : prevIndex - 1
                      )
                    }
                  />
                  <button
                    className={myDayStyles.prevBtn}
                    onClick={() =>
                      setActiveUpdatesIndex((prevIndex) =>
                        prevIndex === displayUpdates.length - 1 ? 0 : prevIndex + 1
                      )
                    }
                  />
                </>
              ) : (
                <>
                  <button
                    className={myDayStyles.nextBtn}
                    onClick={() =>
                      setActiveUpdatesIndex((prevIndex) =>
                        prevIndex === displayUpdates.length - 1 ? 0 : prevIndex + 1
                      )
                    }
                  />
                  <button
                    className={myDayStyles.prevBtn}
                    onClick={() =>
                      setActiveUpdatesIndex((prevIndex) =>
                        prevIndex === 0 ? displayUpdates.length - 1 : prevIndex - 1
                      )
                    }
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
      <Pagination
        items={displayUpdates}
        onClick={(index) => setActiveUpdatesIndex(index)}
        activeIndex={activeUpdatesIndex}
      />
      {access.calendarView && (<>
      <div className={myDayStyles.myDay__section}>
        <h4 className={myDayStyles.myDay__sectionTitle}>{t("REMINDERS")}</h4>
        <ul className={myDayStyles.myDay__swiper}>
          {userAppointments && (
            <Slider
              items={userAppointments}
              className={myDayStyles.notifications__text}
              activeIndex={activeAppointmentsIndex}
              setActiveIndex={setActiveAppointmentsIndex}
            />
          )}

          {(!userAppointments || userAppointments.length === 0) && (
            <li
              className={`${myDayStyles.myDay__no_appointments_sectionCard} ${myDayStyles.myDay__sectionCard}`}
            >
              <div>
                <p className={myDayStyles.myDay__sectionText}>
                  {t("NO_APPOINTMENTS_SCHEDULED_YET")}
                </p>
              </div>
            </li>
          )}
        </ul>
        {userAppointments && userAppointments.length > 1 && (
          <>
            {i18n.dir() === 'ltr' ? (
              <>
                <button
                  className={myDayStyles.nextBtn}
                  onClick={() =>
                    setActiveAppointmentsIndex((prevIndex) =>
                      !userAppointments || prevIndex === 0
                        ? (userAppointments?.length ?? 0) - 1
                        : prevIndex - 1
                    )
                  }
                  disabled={userAppointments.length <= 2}
                />
                <button
                  className={myDayStyles.prevBtn}
                  onClick={() =>
                    setActiveAppointmentsIndex((prevIndex) =>
                      !userAppointments || prevIndex === userAppointments.length - 1
                        ? 0
                        : prevIndex + 1
                    )
                  }
                  disabled={userAppointments.length <= 2}
                />
              </>
            ) : (
              <>
                <button
                  className={myDayStyles.nextBtn}
                  onClick={() =>
                    setActiveAppointmentsIndex((prevIndex) =>
                      !userAppointments || prevIndex === userAppointments.length - 1
                        ? 0
                        : prevIndex + 1
                    )
                  }
                  disabled={userAppointments.length <= 2}
                />
                <button
                  className={myDayStyles.prevBtn}
                  onClick={() =>
                    setActiveAppointmentsIndex((prevIndex) =>
                      !userAppointments || prevIndex === 0
                        ? (userAppointments?.length ?? 0) - 1
                        : prevIndex - 1
                    )
                  }
                  disabled={userAppointments.length <= 2}
                />
              </>
            )}
          </>
        )}
      </div>
      {userAppointments&&userAppointments.length > 1 && <Pagination
        items={userAppointments.map((v) => v.appointments.appointmentDate)}
        onClick={(index) => setActiveAppointmentsIndex(index)}
        activeIndex={activeAppointmentsIndex}
        itemsPerSlide={2}
      />}
      </>)}
      {access.exercisesView && (<>
      <div
        className={`${myDayStyles.myDay__section} ${myDayStyles.myDay__section_practice}`}
      >
        <h4 className={myDayStyles.myDay__sectionTitle}>{t("EXERCISES")}</h4>
        <ul className={myDayStyles.myDay__swiper}>
          {exercises && exercises.length > 0 ? (
            <Slider
              items={exercises}
              className={myDayStyles.notifications__text}
              activeIndex={activeVideoIndex}
              setActiveIndex={setActiveVideoIndex}
            />
          ) : (
            <li
              className={`${myDayStyles.myDay__no_appointments_sectionCard} ${myDayStyles.myDay__sectionCard}`}
            >
              <div>
                <p className={myDayStyles.myDay__sectionText}>
                  {t("NO_EXERCISES_ASSIGNED_YET")}
                </p>
              </div>
            </li>
          )}
        </ul>
        {exercises && exercises.length > 1 && (
          <>
            {i18n.dir() === 'ltr' ? (
              <>
                <button
                  className={myDayStyles.nextBtn}
                  onClick={() =>
                    setActiveVideoIndex((prevIndex) =>
                      prevIndex === 0 ? (exercises?.length ?? 0) - 1 : prevIndex - 1
                    )
                  }
                  disabled={exercises?.length <= 2}
                />
                <button
                  className={myDayStyles.prevBtn}
                  onClick={() =>
                    setActiveVideoIndex((prevIndex) =>
                      prevIndex === exercises.length - 1 ? 0 : prevIndex + 1
                    )
                  }
                  disabled={exercises?.length <= 2}
                />
              </>
            ) : (
              <>
                <button
                  className={myDayStyles.nextBtn}
                  onClick={() =>
                    setActiveVideoIndex((prevIndex) =>
                      prevIndex === exercises.length - 1 ? 0 : prevIndex + 1
                    )
                  }
                  disabled={exercises?.length <= 2}
                />
                <button
                  className={myDayStyles.prevBtn}
                  onClick={() =>
                    setActiveVideoIndex((prevIndex) =>
                      prevIndex === 0 ? (exercises?.length ?? 0) - 1 : prevIndex - 1
                    )
                  }
                  disabled={exercises?.length <= 2}
                />
              </>
            )}
          </>
        )}
      </div>
      {exercises?.length > 1 && (
        <Pagination
          items={exercises.map((v) => v.title)}
          onClick={(index) => setActiveVideoIndex(index)}
          activeIndex={activeVideoIndex}
          itemsPerSlide={mobile ? 1 : 2}
        />
      )}
      </>)}
    </div>
  );
}

export default MyDay;
